"use client";

import { useRouter } from "next/navigation";
import {
  quoteHref,
  type BuilderMatches,
  type BuilderSpec,
  type BuilderSummary,
} from "@/lib/listings/builder-state";
import { ProductDiagram } from "@/components/illustrations/product-diagram";
import { Card } from "@/components/ui/card";
import { GradeRow, type GradePricing } from "./grade-row";
import { LoadPanel } from "./load-panel";
import { SpecControls } from "./spec-controls";
import { useBuilderState } from "./use-builder-state";
import { useGradePricing } from "./use-grade-pricing";
import { YardList } from "./yard-list";

export interface LoadBuilderProps {
  productTypeSlug: string;
  productName: string;
  unitLabel: string;
  truckload: number;
  summary: BuilderSummary;
  pricing: GradePricing[];
  initialSpec: BuilderSpec;
  initialMatches: BuilderMatches;
  initialQuantity: number;
  quantityStep: number;
}

function SectionHeading({ id, title, note }: { id: string; title: string; note?: string }) {
  return (
    <h2 id={id} className="mb-2 text-base font-bold tracking-tight">
      {title}
      {note ? <span className="ml-2 text-xs font-medium text-fg-subtle">{note}</span> : null}
    </h2>
  );
}

function SpecStage({
  productTypeSlug,
  summary,
  spec,
  matches,
  onChange,
}: {
  productTypeSlug: string;
  summary: BuilderSummary;
  spec: BuilderSpec;
  matches: BuilderMatches;
  onChange: (next: Partial<BuilderSpec>) => void;
}) {
  return (
    <Card className="mt-4 rounded-2xl p-2.5" padding="none">
      <div className="relative mx-auto w-full max-w-2xl pt-2">
        <ProductDiagram
          productTypeSlug={productTypeSlug}
          footprint={spec.footprint}
          heightBucket={spec.heightBucket}
          walls={spec.walls}
        />
        <div className="absolute left-1 top-1 rounded-lg bg-chrome px-3 py-2 text-xs text-chrome-muted">
          <span>Listings match this spec</span>
          <b className="block tabular-nums text-2xl font-semibold leading-none text-chrome-foreground">
            {matches.total}
            {matches.truncated && matches.total > 0 ? "+" : ""}
          </b>
        </div>
      </div>
      <SpecControls summary={summary} spec={spec} onChange={onChange} />
    </Card>
  );
}

type BuilderState = ReturnType<typeof useBuilderState>;

function MatchesSection({ state, productTypeSlug }: { state: BuilderState } & Pick<LoadBuilderProps, "productTypeSlug">) {
  const router = useRouter();
  const note = state.isUpdating
    ? "updating…"
    : `${state.matches.total}${state.matches.truncated && state.matches.total > 0 ? "+" : ""} match your spec`;
  return (
    <section className="mt-5" aria-labelledby="yards-heading">
      <SectionHeading id="yards-heading" title="Listings that match" note={note} />
      <YardList
        listings={[
          ...state.picked.filter((pick) => !state.matches.listings.some((listing) => listing._id === pick._id)),
          ...state.matches.listings,
        ]}
        total={state.matches.total}
        truncated={state.matches.truncated}
        dock={state.dock}
        picked={state.picked}
        onTogglePick={state.togglePick}
        onQuoteSpec={() => router.push(quoteHref(productTypeSlug, state.quantity, state.dock, state.spec, state.picked))}
        quotePending={state.dockPending}
        pickingDisabled={state.isUpdating}
      />
    </section>
  );
}

export function LoadBuilder(props: LoadBuilderProps) {
  const { productTypeSlug, productName, unitLabel, truckload, summary } = props;
  const state = useBuilderState(props);
  const pricing = useGradePricing(
    productTypeSlug,
    summary.grades.map((grade) => grade.condition),
    state.quantity,
    props.initialQuantity,
    props.pricing,
  );
  return (
    <div className="grid lg:grid-cols-[1fr_minmax(20rem,24rem)]">
      <LoadPanel
        productTypeSlug={productTypeSlug}
        productName={productName}
        unitLabel={unitLabel}
        truckload={truckload}
        spec={state.spec}
        quantity={state.quantity}
        quantityStep={props.quantityStep}
        onQuantity={state.setQuantity}
        dock={state.dock}
        onDock={state.setDock}
        dockPending={state.dockPending}
        onDockPending={state.setDockPending}
        pricingPending={state.isUpdating}
        picked={state.picked}
        candidates={state.matches.listings}
      />
      <div className="min-w-0 px-4 pb-10 sm:px-6 lg:px-7">
        <SpecStage productTypeSlug={productTypeSlug} summary={summary} spec={state.spec} matches={state.matches} onChange={state.update} />
        <section className="mt-5" aria-labelledby="grade-heading">
          <SectionHeading id="grade-heading" title="Pick a grade" />
          <GradeRow productTypeSlug={productTypeSlug} grades={summary.grades} pricing={pricing} selected={state.spec.conditions} onToggle={state.toggleGrade} />
        </section>
        <MatchesSection state={state} productTypeSlug={productTypeSlug} />
      </div>
    </div>
  );
}
